import React, { useState } from 'react'
import LocalHelmet from '../common/LocalHelmet'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import CalendlyWidget from '../components/CalendlyWidget'
import { submitLead, trackFormSubmission, trackBookingInteraction } from '../utils'
import * as FiIcons from 'react-icons/fi'

const {
  FiMail,
  FiLinkedin,
  FiMessageCircle,
  FiClock,
  FiMapPin,
  FiCheckCircle,
  FiAlertCircle,
  FiCalendar,
  FiLoader
} = FiIcons

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    interests: [],
    otherInterest: '',
    startDate: '',
    budget: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showNewsletter, setShowNewsletter] = useState(false)
  const [newsletterSignup, setNewsletterSignup] = useState(false)
  const [error, setError] = useState('')
  const [showBooking, setShowBooking] = useState(false)

  const handleChange = (e) => {
    const { name, value, checked } = e.target

    if (name === 'interests') {
      setFormData(prev => ({
        ...prev,
        interests: checked
          ? [...prev.interests, value]
          : prev.interests.filter(interest => interest !== value)
      }))
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      trackFormSubmission('contact_form', formData)
      const result = await submitLead(formData)

      if (result.success) {
        setIsSubmitted(true)
        setShowNewsletter(true)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNewsletterSubmit = (e) => {
    e.preventDefault()
    setShowNewsletter(false)
  }

  const interestOptions = [
    'Growth Strategy Advisory',
    'Strategic Measurement', 
    'AI in Practice',
    'Data in Action',
    'Fractional Leadership'
  ]

  const contactMethods = [
    {
      icon: FiMail,
      title: 'Email',
      description: 'info@richardsonstrategy.group',
      action: 'Send an email',
      link: 'mailto:info@richardsonstrategy.group'
    },
    {
      icon: FiLinkedin,
      title: 'LinkedIn',
      description: 'Connect and message on LinkedIn',
      action: 'View profile',
      link: 'https://www.linkedin.com/company/richardson-strategy-group'
    },
    {
      icon: FiCalendar,
      title: 'Quick Call',
      description: 'Schedule a 25-minute conversation',
      action: 'Book a call',
      onClick: () => {
        setShowBooking(!showBooking)
        if (!showBooking) {
          trackBookingInteraction('booking_section_opened')
        }
      }
    }
  ]

  if (showNewsletter) {
    return (
      <>
        <LocalHelmet prioritizeSeoTags>
          <title>Thank You | Richardson Strategy Group</title>
          <meta name="description" content="Thanks for reaching out. We'll get back to you within 24–48 hours." />
          <meta name="robots" content="noindex, nofollow" />
        </LocalHelmet>

        <div className="min-h-screen py-12 md:py-20 flex items-center justify-center">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-secondary-100"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">Message Received!</h2>
              <p className="text-base md:text-lg text-secondary-600 mb-6 leading-relaxed">
                Thanks for reaching out. We'll get back to you within 24-48 hours with thoughts on your growth strategy needs.
              </p>

              <div className="bg-secondary-50 p-4 md:p-6 rounded-lg mb-6">
                <p className="text-sm text-secondary-700 mb-4 font-medium">
                  Want to stay updated with strategic insights? (We promise no more than 2x per month)
                </p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <div className="flex items-start space-x-3 text-left">
                    <input
                      type="checkbox"
                      id="newsletter"
                      checked={newsletterSignup}
                      onChange={(e) => setNewsletterSignup(e.target.checked)}
                      className="mt-1 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="newsletter" className="text-sm text-secondary-600 flex-1">
                      Yes, send me strategic insights at: <strong>{formData.email}</strong>
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    {newsletterSignup ? 'Sign Me Up!' : 'No Thanks, Just Contact Me'}
                  </button>
                </form>
              </div>

              <p className="text-secondary-500 text-sm">
                In the meantime, feel free to connect with me on LinkedIn or send a direct email.
              </p>
            </motion.div>
          </div>
        </div>
      </>
    )
  }

  if (isSubmitted && !showNewsletter) {
    return (
      <>
        <LocalHelmet prioritizeSeoTags>
          <title>Thank You | Richardson Strategy Group</title>
          <meta name="description" content="Thank you for reaching out. We'll be in touch soon." />
          <meta name="robots" content="noindex, nofollow" />
        </LocalHelmet>

        <div className="min-h-screen py-12 md:py-20 flex items-center justify-center">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-secondary-100"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">All Set!</h2>
              <p className="text-base md:text-lg text-secondary-600 mb-6 leading-relaxed">
                We'll be in touch soon. Looking forward to our conversation!
              </p>
            </motion.div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
        <title>Contact | Richardson Strategy Group</title>
        <meta name="description" content="Ready to move ahead? Reach out for direct support, consulting, or a real conversation about growth strategy, analytics, and change." />
        <link rel="canonical" href="https://richardsonstrategy.group/contact" />
        <meta property="og:title" content="Contact | Richardson Strategy Group" />
        <meta property="og:description" content="Contact Richardson Strategy Group for business growth, strategy, AI, and analytics advisory." />
        <meta property="og:url" content="https://richardsonstrategy.group/contact" />
        <meta property="og:image" content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-contact.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </LocalHelmet>

      <div className="min-h-screen py-12 md:py-20" id="contact-form">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4 md:mb-6"
            >
              Let's Talk About Your Growth Strategy
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg md:text-xl text-secondary-600 max-w-3xl mx-auto leading-relaxed"
            >
              No jargon. No guesswork. A conversation that starts with clarity about where you are, where you want to go, and how to get there.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-secondary-100"
            >
              <h2 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4 md:mb-6">Start the Conversation</h2>
              
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-secondary-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-secondary-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-secondary-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Your company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">What are you interested in? (Select all that apply)</label>
                  <div className="space-y-2">
                    {interestOptions.map((interest) => (
                      <label key={interest} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="interests"
                          value={interest}
                          checked={formData.interests.includes(interest)}
                          onChange={handleChange}
                          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-secondary-700 mb-2">Tell me about your growth strategy challenge</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="What's keeping you up at night?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Message</span>
                  )}
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-6 md:space-y-8"
            >
              <div className="bg-primary-50 p-4 md:p-6 rounded-2xl">
                <h3 className="text-lg md:text-xl font-semibold text-secondary-900 mb-4">Prefer to reach out directly?</h3>
                <div className="space-y-4">
                  {contactMethods.map((method) => (
                    <div
                      key={method.title}
                      onClick={method.onClick}
                      className={`flex items-center space-x-3 p-3 bg-white rounded-lg transition-shadow ${
                        method.onClick ? 'cursor-pointer hover:shadow-sm' : ''
                      }`}
                    >
                      {method.link ? (
                        <a
                          href={method.link}
                          target={method.link.startsWith('http') ? '_blank' : '_self'}
                          rel={method.link.startsWith('http') ? 'noopener noreferrer' : ''}
                          className="flex items-center space-x-3 w-full"
                        >
                          <SafeIcon icon={method.icon} className="w-5 h-5 text-primary-600" />
                          <div>
                            <div className="font-medium text-secondary-900 text-sm md:text-base">{method.title}</div>
                            <div className="text-secondary-600 text-xs md:text-sm">{method.description}</div>
                          </div>
                        </a>
                      ) : (
                        <>
                          <SafeIcon icon={method.icon} className="w-5 h-5 text-primary-600" />
                          <div>
                            <div className="font-medium text-secondary-900 text-sm md:text-base">{method.title}</div>
                            <div className="text-secondary-600 text-xs md:text-sm">{method.description}</div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {showBooking && (
                <CalendlyWidget inline={true} />
              )}

              <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-secondary-100">
                <h3 className="text-lg md:text-xl font-semibold text-secondary-900 mb-4">What to Expect</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-600 mt-1" />
                    <div>
                      <div className="font-medium text-secondary-900 text-sm md:text-base">Quick Response</div>
                      <div className="text-secondary-600 text-xs md:text-sm">I'll get back to you within 24 hours</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-primary-600 mt-1" />
                    <div>
                      <div className="font-medium text-secondary-900 text-sm md:text-base">Real Conversation</div>
                      <div className="text-secondary-600 text-xs md:text-sm">No sales pitch, just honest discussion about your challenges</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600 mt-1" />
                    <div>
                      <div className="font-medium text-secondary-900 text-sm md:text-base">Based in Philadelphia</div>
                      <div className="text-secondary-600 text-xs md:text-sm">Serving clients nationwide, remotely and on-site</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Contact